// ─────────────────────────────────────────────────────────────────────────────
// TransparencyPanel – "Show data Claude saw"
// Lets the user inspect exactly what's currently in the assistant's view
// of the page (registered widgets, ESG signal, last intercepted API calls).
// Sits between the messages list and the input area.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from "react";
import type { PageContext } from "../types";

interface TransparencyPanelProps {
	context: PageContext;
}

export default function TransparencyPanel({ context }: TransparencyPanelProps) {
	const [open, setOpen] = useState(false);
	const widgetCount = context.widgetIndex.length;
	const apiCount = context.apiData.length;

	return (
		<div
			style={{
				borderTop: "1px solid #EEE7F6",
				background: "#FCFBFE",
				padding: open ? "10px 14px 12px" : "6px 14px",
				flexShrink: 0,
			}}
		>
			<button
				className="cai-transparency-toggle"
				onClick={() => setOpen((o) => !o)}
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					gap: 6,
					justifyContent: "space-between",
					textAlign: "left",
				}}
			>
				<span style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<span style={{ fontSize: 11 }}>ⓘ</span>
					<span>Show data the assistant saw</span>
				</span>
				<span style={{ color: "#94a3b8", fontSize: 10 }}>
					{widgetCount} widget{widgetCount === 1 ? "" : "s"}
					{apiCount > 0 ? ` · ${apiCount} api` : ""} {open ? "▲" : "▼"}
				</span>
			</button>

			{open && (
				<div
					className="cai-scroll"
					style={{
						marginTop: 8,
						maxHeight: 200,
						overflowY: "auto",
						fontSize: 11,
						color: "#475569",
						lineHeight: 1.5,
					}}
				>
					<Section label="Page identity">
						<div>
							<strong style={{ color: "#1E1730" }}>{context.dom.title}</strong>
						</div>
						<div style={{ wordBreak: "break-all", color: "#94a3b8" }}>
							{context.dom.url}
						</div>
					</Section>

					{context.esg && (
						<Section label="ESG signal">
							<div>
								<strong>{context.esg.domain.replace(/_/g, " ")}</strong>
								{context.esg.frameworks.length > 0 &&
									" · " + context.esg.frameworks.join(", ")}
							</div>
							{context.esg.topics.length > 0 && (
								<div style={{ color: "#64748b" }}>
									Topics: {context.esg.topics.join(", ")}
								</div>
							)}
						</Section>
					)}

					{widgetCount > 0 && (
						<Section label={`Registered widgets (${widgetCount})`}>
							{context.widgetIndex.slice(0, 30).map((w) => {
								const sizeStr = w.rowCount
									? `${w.rowCount} rows`
									: w.seriesCount
									? `${w.seriesCount} series`
									: "—";
								return (
									<div
										key={w.widgetId}
										style={{
											padding: "3px 0",
											borderBottom: "1px dashed #EEE7F6",
											display: "flex",
											gap: 6,
											flexWrap: "wrap",
										}}
									>
										<span
											style={{
												fontFamily:
													"ui-monospace, SFMono-Regular, Menlo, monospace",
												fontSize: 10,
												color: "#94a3b8",
											}}
										>
											{w.widgetId.slice(0, 14)}
										</span>
										<span style={{ color: "#1E1730", flex: 1 }}>
											{w.title}
										</span>
										<span
											style={{
												color: "#482A7A",
												fontWeight: 600,
												fontSize: 10,
											}}
										>
											{w.kind} · {sizeStr}
										</span>
									</div>
								);
							})}
							{widgetCount > 30 && (
								<div style={{ color: "#94a3b8", paddingTop: 4 }}>
									+{widgetCount - 30} more not shown
								</div>
							)}
						</Section>
					)}

					{apiCount > 0 && (
						<Section label={`Recent intercepted API responses (${apiCount})`}>
							{context.apiData.slice(-3).map((d, i) => (
								<div
									key={i}
									style={{
										padding: "3px 0",
										fontFamily:
											"ui-monospace, SFMono-Regular, Menlo, monospace",
										fontSize: 10,
										color: "#64748b",
										wordBreak: "break-all",
									}}
								>
									[{d.method}] {d.url.slice(0, 80)}
								</div>
							))}
						</Section>
					)}

					<div
						style={{
							marginTop: 8,
							padding: "6px 8px",
							background: "#F5F3F8",
							borderRadius: 6,
							color: "#482A7A",
							fontSize: 10,
						}}
					>
						All of the above is wrapped in a{" "}
						<code
							style={{
								fontFamily:
									"ui-monospace, SFMono-Regular, Menlo, monospace",
							}}
						>
							&lt;page_context untrusted="true"&gt;
						</code>{" "}
						block before being sent — the assistant treats it as data, not
						instructions.
					</div>
				</div>
			)}
		</div>
	);
}

function Section({
	label,
	children,
}: {
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div style={{ marginBottom: 8 }}>
			<div
				style={{
					fontSize: 9.5,
					textTransform: "uppercase",
					letterSpacing: "0.05em",
					color: "#94a3b8",
					fontWeight: 700,
					marginBottom: 3,
				}}
			>
				{label}
			</div>
			{children}
		</div>
	);
}
